import { PatternFeature } from '@kb3d/models/classes/features/pattern-feature';
import { FeatureRenderer, SubmeshVertexDataMap } from './feature';
import { FeatureGeometryProcessor } from './geometry-processor/base-processor';
import { type PatternGeometryWorkerPayload } from './geometry-processor/pattern-geometry';
import { type SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { SketchNode } from '@kb3d/models/classes/meshes/sketch-node';
import { Vector3 } from '@babylonjs/core/Maths/math';
import { IKbVectorLike, KbVector } from '@kb3d/models/classes/primitives/kb-vector';

export abstract class PatternRenderer extends FeatureRenderer<PatternFeature> {
    update3dInternal() { }

    async makeWorkerPayload(feature: PatternFeature, geometryData: SubmeshVertexDataMap) {
        const payload = await super.makeWorkerPayload(feature, geometryData);
        const targetMeshNode = feature._manager?.getById<SpaceNode>(feature.meshId);
        if (!targetMeshNode) {
            return payload;
        }
        const targetMesh = this.viewer.getRendererNode(targetMeshNode);
        const thisMesh = this.viewer.getRendererNode(feature._parent!);
        const otherMeshVertexData = geometryData.get(targetMeshNode._dynamicId);
        if (!targetMesh || !thisMesh || !otherMeshVertexData || !(targetMesh instanceof Mesh)) {
            return payload;
        }
        
        if(!feature.meshId) {
            const featureData: PatternGeometryWorkerPayload = {
                thisMeshTransform: thisMesh.computeWorldMatrix(true).toArray(),
            };

            payload.featureData = featureData;
            return payload;
        }

        const sketchNode = this.viewer.getNode(feature.meshId);
        let sketchPoints = new Float32Array();
        if (sketchNode instanceof SketchNode) {
            let distance = feature.distance;
            if (distance <= 0) {
                distance = 1;
            }
            const positionArray = this.getPositionArray(
                sketchNode,
                feature.distance,
                feature.addEndMesh
            );
            sketchPoints = new Float32Array(positionArray.length * 3);
            positionArray.forEach((point, index) => {
                sketchPoints[index * 3] = point.x;
                sketchPoints[index * 3 + 1] = point.y;
                sketchPoints[index * 3 + 2] = point.z;
            }
            );
        }

        const featureData: PatternGeometryWorkerPayload = {
            sketchPoints,
            otherMeshTransform: targetMesh.computeWorldMatrix(true).toArray(),
            thisMeshTransform: thisMesh.computeWorldMatrix(true).toArray(),
        };

        payload.featureData = featureData;
        return payload;
    }

    processor: FeatureGeometryProcessor<PatternFeature>;

    dispose() {
        super.dispose();
    }

    protected getPositionArray(node: SketchNode, distance: number, addEndMesh: boolean) {
        const result: Vector3[] = [];
        const sketchLength = node.getSketchLength();
        const numberOfMeshes = sketchLength / distance;
        const percentagePoints: number[] = [];
        for (let i = 0; i < numberOfMeshes; i++) {
            percentagePoints.push((1 / numberOfMeshes) * i);
        }

        percentagePoints.forEach(point => {
            result.push(this.getIntervalPosition(node, point));
        });

        if (addEndMesh) {
            const lastPath = node.paths[node.paths.length - 1];
            const lastPoint = lastPath._points[lastPath._points.length - 1];
            const lastPointVec3 = new Vector3(lastPoint.x, lastPoint.y, lastPoint.z);
            result.push(lastPointVec3);
        }

        return result;
    }
    protected getIntervalPosition(node: SketchNode, posZeroToOne: number) {
        const result = new Vector3(0, 0, 0);
        const sketchLength = node.getSketchLength();
        let trackedLength = 0;
        let targetNotFound = true;
        node.paths.forEach(path => {
            let lastPosition: IKbVectorLike | null = null;
            let lastLength = 0;
            path._points.forEach(point => {
                if (!lastPosition) {
                    lastPosition = point;
                } else {
                    const currentPos = point;
                    const distance = Math.sqrt(
                        Math.pow(lastPosition.x - currentPos.x, 2) +
                        Math.pow(lastPosition.y - currentPos.y, 2) +
                        Math.pow(lastPosition.z - currentPos.z, 2)
                    );
                    lastLength = trackedLength;
                    trackedLength += distance;
                    const distanceBetweenPoints = trackedLength - lastLength;
                    const distanceFromP1toTarget = posZeroToOne * sketchLength - lastLength;
                    const percentageBetween2Points = distanceFromP1toTarget / distanceBetweenPoints;
                    const vectorBetween2Points = new KbVector(
                        currentPos.x - lastPosition.x,
                        currentPos.y - lastPosition.y,
                        currentPos.z - lastPosition.z
                    );
                    if (trackedLength > posZeroToOne * sketchLength && targetNotFound) {
                        const calculatedPosition = vectorBetween2Points
                            .scale(percentageBetween2Points)
                            .add(lastPosition);
                        result.set(calculatedPosition.x, calculatedPosition.y, calculatedPosition.z);
                        targetNotFound = false;
                    }
                    lastPosition = currentPos;
                }
            });
            if (posZeroToOne === 1) {
                const path = node.paths[node.paths.length - 1];
                const controlPoint = path._points[path._points.length - 1];
                result.set(controlPoint.x, controlPoint.y, controlPoint.z);
            }
        });

        return result;
    }
}
